import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { StateManagementService } from "providers/state-management/state-management.service";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { from } from "rxjs/observable/from";
import { race } from "rxjs/observable/race";
import { _throw as ObservableThrow } from "rxjs/observable/throw"; // Correct import for Observable.throw
import { catchError, filter, finalize, switchMap, take } from "rxjs/operators";
import { AuthService } from "../auth/auth.service";
import { LocationService } from "../location/location.service";

@Injectable()
export class AuthorizationHttpInterceptor implements HttpInterceptor {
  private tokenRefreshedSubject: BehaviorSubject<string | null>;

  /**
   * CO-3825: publishes the failure of an in-flight token refresh, so requests
   * queued behind it are released.
   *
   * They cannot be released through tokenRefreshedSubject. A failed refresh
   * publishes `null` there, and every waiter is filtered on `token !== null`
   * precisely so it does not mistake "no token yet" for a token — so null
   * releases nobody and the queue waits forever. The subject is shared
   * app-wide, so it cannot be errored or completed to signal this either;
   * that would break it permanently for every later refresh.
   */
  private refreshFailedSubject = new Subject<any>();

  constructor(
    private authService: AuthService,
    private locationService: LocationService,
    private injector: Injector,
    private stateManagementService: StateManagementService
  ) {
    this.tokenRefreshedSubject =
      this.stateManagementService.getTokenRefreshedSubject();
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (
      this.stateManagementService.isRefreshingToken ||
      this.stateManagementService.isManualTokenRefreshInProgress
    ) {
      return this.awaitRefreshedToken(req, next).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            return this.handle401Error(req, next);
          }
          return ObservableThrow(error);
        })
      );
    } else {
      req = this.addAuthorizationHeader(req);
      return next.handle(req).pipe(
        catchError((error: HttpErrorResponse) => {
          if (
            error.status === 401 ||
            (error.status === 0 &&
              !this.stateManagementService.isRefreshingToken &&
              !this.stateManagementService.isManualTokenRefreshInProgress)
          ) {
            return this.handle401Error(req, next);
          } else if (error.status === 400 && req.url.includes("cognito")) {
            this.authService.signOut(
              "Your session has expired. Please log in again to continue."
            );
            const customError = new Error(
              `Token refresh failed: ${error.message || error}`
            );
            (customError as any).code = "REFRESH_TOKEN_FAILED";
            return ObservableThrow(error);
          } else if (
            error.status === 400 &&
            this.isMissingAuthorizationHeaderError(error)
          ) {
            // NOVA-1933: the request went out with no Authorization header (empty
            // cached token) and API Gateway rejected it with a 400. Treat it like a
            // 401 — refresh-then-replay (or the Session Expired alert if the refresh
            // fails) — instead of re-throwing it raw to a silent consumer.
            return this.handle401Error(req, next);
          } else {
            return ObservableThrow(error);
          }
        })
      );
    }
  }

  private addAuthorizationHeader(req: HttpRequest<any>): HttpRequest<any> {
    let headers = req.headers;
    const token = this.authService.getAuthorizationHeaderValue();
    headers = headers.set("X-AUTH-MODE", "cognito");

    if (token && token !== "null") {
      headers = headers.set("Authorization", `Bearer ${token}`);
    }

    const position = this.locationService.getPosition() || "UNKNOWN";
    headers = headers.set("Geolocation", position);
    return req.clone({ headers });
  }

  // NOVA-1933: detect the AWS API Gateway request-validation 400 that fires when a
  // request reaches the gateway with no Authorization header (body message
  // "Missing required request parameters: [Authorization]"). This is distinct from
  // a malformed/expired token (401) and from the Amplify cognito-400 refresh case.
  private isMissingAuthorizationHeaderError(
    error: HttpErrorResponse
  ): boolean {
    const message =
      error && error.error && typeof error.error.message === "string"
        ? error.error.message
        : "";
    return (
      message.indexOf("Missing required request parameters") !== -1 &&
      message.indexOf("Authorization") !== -1
    );
  }

  /**
   * Wait for the in-flight refresh, then replay `req` with the new token.
   *
   * Raced against refreshFailedSubject so a refresh that FAILS releases the
   * queue with that failure instead of stranding it. The token branch wins on
   * its first emission (the replayed response) and the loser is unsubscribed.
   */
  private awaitRefreshedToken(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return race(
      this.tokenRefreshedSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addToken(req, token!)))
      ),
      this.refreshFailedSubject.pipe(
        take(1),
        switchMap((error) => ObservableThrow(error))
      )
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (
      !this.stateManagementService.isRefreshingToken &&
      !this.stateManagementService.isManualTokenRefreshInProgress
    ) {
      this.stateManagementService.isRefreshingToken = true;
      this.tokenRefreshedSubject.next(null);

      return from(this.authService.refreshAccessToken()).pipe(
        // CO-3825: this handler guards the TOKEN REFRESH, and only that. It has
        // to sit BEFORE the switchMap — placed after it, it also catches
        // failures of the REPLAYED request, and a replay that fails for a
        // non-auth reason (an unreachable server, a 5xx) is not an expired
        // session. That mis-scope is what signed clinicians out mid-chart when
        // one non-critical dependency was unreachable: refreshAccessToken()
        // SUCCEEDS (Cognito is a different host, still up), the replay fails
        // again, the inner catchError rethrows it, and it landed here.
        catchError((error) => {
          this.stateManagementService.isRefreshingToken = false;
          this.tokenRefreshedSubject.next(null);
          // CO-3825: release anything queued behind this refresh. Without it
          // they wait on a token that is never coming.
          this.refreshFailedSubject.next(error);
          this.authService.signOut(
            "Your session has expired. Please log in again to continue."
          );
          return ObservableThrow(error);
        }),
        switchMap((newAccessToken) => {
          this.stateManagementService.isRefreshingToken = false;
          if (newAccessToken) {
            this.tokenRefreshedSubject.next(newAccessToken);
            return next.handle(this.addToken(req, newAccessToken)).pipe(
              catchError((error: HttpErrorResponse) => {
                if (error.status === 401) {
                  // A freshly minted token was still rejected: the session
                  // genuinely is over.
                  this.authService.signOut(
                    "Your session has expired. Please log in again to continue."
                  );
                }
                // Anything else — status 0, a 5xx, a timeout — is a transport
                // failure of THIS request, not an authentication problem.
                // Surface it to the caller unchanged.
                //
                // (Returning `null` here, as this branch used to, does not
                // strand the stream: RxJS 5 substitutes its own "You provided
                // 'null' where a stream was expected" TypeError, so the caller
                // received an RxJS internal in place of the HttpErrorResponse
                // it is written to read.)
                return ObservableThrow(error);
              })
            );
          } else {
            this.authService.signOut(
              "Your session has expired. Please log in again to continue."
            );
            const error = new HttpErrorResponse({
              status: 401,
              statusText: "Unauthorized",
            });
            return ObservableThrow(error);
          }
        }),
        finalize(() => {
          this.stateManagementService.isRefreshingToken = false;
        })
      );
    } else {
      return this.awaitRefreshedToken(req, next).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.authService.signOut(
              "Your session has expired. Please log in again to continue."
            );
          }
          // CO-3825: same `return null` defect as the replay branch above.
          return ObservableThrow(error);
        })
      );
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/authorization/AuthorizationHttpInterceptor.ts